import type { ResumeDto } from "@reactive-resume/dto";
import { useCallback, useEffect, useRef, useState } from "react";

import { updateResume } from "@/client/services/resume";

const AUTOSAVE_DEBOUNCE_MS = 500;

export type AutoSaveStatus = "saved" | "saving" | "error";

export const useAutoSave = (resume: ResumeDto) => {
  const [status, setStatus] = useState<AutoSaveStatus>("saved");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const id = useRef<string>();
  const previous = useRef<ResumeDto | null>(null);
  const pending = useRef<ResumeDto | null>(null);
  const ready = useRef(false);
  const failed = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const active = useRef<Promise<void> | null>(null);

  const latest = () => pending.current;

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const drain = useCallback((): Promise<void> => {
    if (active.current) return active.current;
    if (!ready.current || !pending.current || failed.current) return Promise.resolve();
    const currentGeneration = generation.current;
    const run = async () => {
      while (ready.current && pending.current && !failed.current) {
        const snapshot = pending.current;
        pending.current = null;
        ready.current = false;
        try {
          await updateResume(snapshot);
          if (!mounted.current || generation.current !== currentGeneration) return;
          if (!latest()) {
            setStatus("saved");
            setLastSavedAt(new Date());
          }
        } catch {
          if (!mounted.current || generation.current !== currentGeneration) return;
          pending.current = latest() ?? snapshot;
          failed.current = true;
          setStatus("error");
        }
      }
    };
    const promise = run().finally(() => {
      if (active.current === promise) active.current = null;
    });
    active.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    if (!resume.id) return;
    if (id.current !== resume.id) {
      generation.current += 1;
      id.current = resume.id;
      previous.current = resume;
      pending.current = null;
      ready.current = false;
      failed.current = false;
      active.current = null;
      clearTimer();
      setStatus("saved");
      setLastSavedAt(null);
      return;
    }
    if (previous.current === resume) return;
    previous.current = resume;
    pending.current = structuredClone(resume);
    if (!failed.current) {
      setStatus("saving");
      clearTimer();
      timer.current = setTimeout(() => {
        ready.current = true;
        void drain();
      }, AUTOSAVE_DEBOUNCE_MS);
    }
  }, [resume, drain]);

  const retry = useCallback(async () => {
    if (!pending.current) return;
    failed.current = false;
    clearTimer();
    ready.current = true;
    setStatus("saving");
    await drain();
  }, [drain]);

  const flush = useCallback(async () => {
    clearTimer();
    if (pending.current && !failed.current) ready.current = true;
    if (active.current) await active.current;
    if (pending.current && !failed.current) await drain();
    return !pending.current && !failed.current;
  }, [drain]);

  useEffect(() => {
    mounted.current = true;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!pending.current && !active.current) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      mounted.current = false;
      window.removeEventListener("beforeunload", onBeforeUnload);
      clearTimer();
    };
  }, []);

  return { status, lastSavedAt, retry, flush };
};
